/**
 * Safety Reports Page
 * BuilderBel Construction Project Management System
 *
 * Tab-based view: Inspections | Toolbox Talks | Observations
 * Stats dashboard at top + DataGrid per tab + drawers for CRUD.
 */

import {
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Eye,
  MessageSquare,
  Pencil,
  Shield,
  Trash2,
  X,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import SafetyInspectionDrawer from '../components/safetyReports/SafetyInspectionDrawer';
import SafetyObservationDrawer from '../components/safetyReports/SafetyObservationDrawer';
import ToolboxTalkDrawer from '../components/safetyReports/ToolboxTalkDrawer';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useGetSafetyStatsQuery,
  useGetInspectionsQuery,
  useGetToolboxTalksQuery,
  useGetObservationsQuery,
  useDeleteInspectionMutation,
  useDeleteToolboxTalkMutation,
  useDeleteObservationMutation,
  useCompleteInspectionMutation,
  useCompleteToolboxTalkMutation,
  useResolveObservationMutation,
  useCloseInspectionMutation,
} from '../services/safetyApi';
import type {
  SafetyInspection,
  ToolboxTalk,
  SafetyObservation,
  InspectionStatus,
  InspectionOverallStatus,
  ObservationSeverity,
  ObservationStatus,
  ToolboxTalkCategory,
  InspectionType,
} from '../types/safety';

// ============================================================================
// Constants
// ============================================================================

type ActiveTab = 'inspections' | 'toolbox_talks' | 'observations';

const INSPECTION_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'site', label: 'Site' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'scaffold', label: 'Scaffold' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'excavation', label: 'Excavation' },
  { value: 'fire_safety', label: 'Fire Safety' },
  { value: 'housekeeping', label: 'Housekeeping' },
  { value: 'ppe', label: 'PPE' },
  { value: 'other', label: 'Other' },
];

const INSPECTION_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'closed', label: 'Closed' },
];

const TALK_CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'fall_protection', label: 'Fall Protection' },
  { value: 'ppe', label: 'PPE' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'excavation', label: 'Excavation' },
  { value: 'scaffold', label: 'Scaffold' },
  { value: 'fire', label: 'Fire Safety' },
  { value: 'chemical', label: 'Chemical' },
  { value: 'confined_space', label: 'Confined Space' },
  { value: 'heavy_equipment', label: 'Heavy Equipment' },
  { value: 'weather', label: 'Weather' },
  { value: 'housekeeping', label: 'Housekeeping' },
  { value: 'general', label: 'General' },
];

const OBSERVATION_SEVERITY_FILTER_OPTIONS = [
  { value: '', label: 'All Severities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const OBSERVATION_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getInspectionStatusVariant = (
  status: InspectionStatus
): 'success' | 'warning' | 'info' | 'neutral' | 'error' => {
  switch (status) {
    case 'closed':
      return 'success';
    case 'reviewed':
      return 'info';
    case 'completed':
      return 'warning';
    case 'in_progress':
      return 'info';
    case 'draft':
    default:
      return 'neutral';
  }
};

const getInspectionStatusLabel = (status: InspectionStatus): string => {
  const labels: Record<InspectionStatus, string> = {
    draft: 'Draft',
    in_progress: 'In Progress',
    completed: 'Completed',
    reviewed: 'Reviewed',
    closed: 'Closed',
  };
  return labels[status] || status;
};

const getOverallStatusBadge = (status: InspectionOverallStatus): string => {
  switch (status) {
    case 'satisfactory':
      return 'bg-green-100 text-green-700';
    case 'needs_improvement':
      return 'bg-yellow-100 text-yellow-700';
    case 'unsatisfactory':
      return 'bg-orange-100 text-orange-700';
    case 'critical':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const getObservationSeverityBadge = (severity: ObservationSeverity): string => {
  switch (severity) {
    case 'critical':
      return 'bg-red-100 text-red-700';
    case 'high':
      return 'bg-orange-100 text-orange-700';
    case 'medium':
      return 'bg-yellow-100 text-yellow-700';
    case 'low':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const getObservationStatusVariant = (
  status: ObservationStatus
): 'success' | 'warning' | 'info' | 'neutral' | 'error' => {
  switch (status) {
    case 'resolved':
    case 'closed':
      return 'success';
    case 'in_progress':
      return 'info';
    case 'open':
    default:
      return 'warning';
  }
};

const formatInspectionType = (type: InspectionType): string => {
  const labels: Record<InspectionType, string> = {
    site: 'Site',
    equipment: 'Equipment',
    scaffold: 'Scaffold',
    electrical: 'Electrical',
    excavation: 'Excavation',
    fire_safety: 'Fire Safety',
    housekeeping: 'Housekeeping',
    ppe: 'PPE',
    other: 'Other',
  };
  return labels[type] || type;
};

const formatTalkCategory = (category: ToolboxTalkCategory): string => {
  const labels: Record<ToolboxTalkCategory, string> = {
    fall_protection: 'Fall Protection',
    ppe: 'PPE',
    electrical: 'Electrical',
    excavation: 'Excavation',
    scaffold: 'Scaffold',
    fire: 'Fire Safety',
    chemical: 'Chemical',
    confined_space: 'Confined Space',
    heavy_equipment: 'Heavy Equipment',
    weather: 'Weather',
    housekeeping: 'Housekeeping',
    general: 'General',
  };
  return labels[category] || category;
};

// ============================================================================
// Main Page Component
// ============================================================================

const SafetyReports: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState<ActiveTab>('inspections');

  // Inspection state
  const [inspectionSearch, setInspectionSearch] = useState('');
  const [filterInspectionType, setFilterInspectionType] = useState<InspectionType | ''>('');
  const [filterInspectionStatus, setFilterInspectionStatus] = useState<InspectionStatus | ''>('');
  const [inspectionPage, setInspectionPage] = useState(1);
  const [selectedInspection, setSelectedInspection] = useState<SafetyInspection | null>(null);
  const [isInspectionDrawerOpen, setIsInspectionDrawerOpen] = useState(false);
  const [inspectionDrawerMode, setInspectionDrawerMode] = useState<'create' | 'edit'>('create');

  // Toolbox talk state
  const [talkSearch, setTalkSearch] = useState('');
  const [filterTalkCategory, setFilterTalkCategory] = useState<ToolboxTalkCategory | ''>('');
  const [talkPage, setTalkPage] = useState(1);
  const [selectedTalk, setSelectedTalk] = useState<ToolboxTalk | null>(null);
  const [isTalkDrawerOpen, setIsTalkDrawerOpen] = useState(false);
  const [talkDrawerMode, setTalkDrawerMode] = useState<'create' | 'edit'>('create');

  // Observation state
  const [observationSearch, setObservationSearch] = useState('');
  const [filterObsSeverity, setFilterObsSeverity] = useState<ObservationSeverity | ''>('');
  const [filterObsStatus, setFilterObsStatus] = useState<ObservationStatus | ''>('');
  const [observationPage, setObservationPage] = useState(1);
  const [selectedObservation, setSelectedObservation] = useState<SafetyObservation | null>(null);
  const [isObsDrawerOpen, setIsObsDrawerOpen] = useState(false);
  const [obsDrawerMode, setObsDrawerMode] = useState<'create' | 'edit'>('create');

  const LIMIT = 20;

  // ============================================================================
  // API Calls
  // ============================================================================

  const { data: statsResponse } = useGetSafetyStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const {
    data: inspectionsResponse,
    isLoading: isLoadingInspections,
    isFetching: isFetchingInspections,
    refetch: refetchInspections,
  } = useGetInspectionsQuery(
    {
      projectId: projectId || '',
      params: {
        page: inspectionPage,
        limit: LIMIT,
        search: inspectionSearch || undefined,
        inspectionType: filterInspectionType || undefined,
        status: filterInspectionStatus || undefined,
        sortBy: 'inspectionDate',
        sortOrder: 'desc',
      },
    },
    { skip: !projectId || activeTab !== 'inspections' }
  );

  const {
    data: talksResponse,
    isLoading: isLoadingTalks,
    isFetching: isFetchingTalks,
    refetch: refetchTalks,
  } = useGetToolboxTalksQuery(
    {
      projectId: projectId || '',
      params: {
        page: talkPage,
        limit: LIMIT,
        search: talkSearch || undefined,
        category: filterTalkCategory || undefined,
        sortBy: 'talkDate',
        sortOrder: 'desc',
      },
    },
    { skip: !projectId || activeTab !== 'toolbox_talks' }
  );

  const {
    data: observationsResponse,
    isLoading: isLoadingObservations,
    isFetching: isFetchingObservations,
    refetch: refetchObservations,
  } = useGetObservationsQuery(
    {
      projectId: projectId || '',
      params: {
        page: observationPage,
        limit: LIMIT,
        search: observationSearch || undefined,
        severity: filterObsSeverity || undefined,
        status: filterObsStatus || undefined,
        sortBy: 'observationDate',
        sortOrder: 'desc',
      },
    },
    { skip: !projectId || activeTab !== 'observations' }
  );

  const [deleteInspection] = useDeleteInspectionMutation();
  const [completeInspection] = useCompleteInspectionMutation();
  const [closeInspection] = useCloseInspectionMutation();
  const [deleteTalk] = useDeleteToolboxTalkMutation();
  const [completeTalk] = useCompleteToolboxTalkMutation();
  const [deleteObservation] = useDeleteObservationMutation();
  const [resolveObservation] = useResolveObservationMutation();

  // ============================================================================
  // Computed Data
  // ============================================================================

  const stats = statsResponse?.data;
  const inspections = useMemo(
    () => inspectionsResponse?.data?.inspections || [],
    [inspectionsResponse]
  );
  const inspectionPagination = inspectionsResponse?.data?.pagination;

  const talks = useMemo(() => talksResponse?.data?.talks || [], [talksResponse]);
  const talkPagination = talksResponse?.data?.pagination;

  const observations = useMemo(
    () => observationsResponse?.data?.observations || [],
    [observationsResponse]
  );
  const observationPagination = observationsResponse?.data?.pagination;

  // Header stats
  const headerStats = useMemo(() => {
    if (!stats) return [];
    return [
      {
        title: 'Inspections',
        value: stats.totalInspections,
        variant: 'brand' as const,
      },
      {
        title: 'Avg Score',
        value: `${stats.averageScore}%`,
        variant: stats.averageScore >= 90 ? ('success' as const) : ('warning' as const),
      },
      {
        title: 'Open Obs.',
        value: stats.openObservations,
        variant: stats.openObservations > 0 ? ('warning' as const) : ('success' as const),
      },
      {
        title: 'Talks (this mo.)',
        value: stats.toolboxTalksThisMonth,
        variant: 'info' as const,
      },
    ];
  }, [stats]);

  // ============================================================================
  // Handlers - Inspections
  // ============================================================================

  const handleCreateInspection = useCallback(() => {
    setSelectedInspection(null);
    setInspectionDrawerMode('create');
    setIsInspectionDrawerOpen(true);
  }, []);

  const handleEditInspection = useCallback((inspection: SafetyInspection) => {
    setSelectedInspection(inspection);
    setInspectionDrawerMode('edit');
    setIsInspectionDrawerOpen(true);
  }, []);

  const handleDeleteInspection = useCallback(
    async (inspection: SafetyInspection) => {
      if (!projectId) return;
      if (
        !window.confirm(
          `Delete inspection ${inspection.inspectionNumber}? This cannot be undone.`
        )
      ) {
        return;
      }
      try {
        await deleteInspection({
          projectId,
          inspectionId: inspection._id,
        }).unwrap();
        toast.success('Inspection deleted');
      } catch {
        toast.error('Failed to delete inspection');
      }
    },
    [projectId, deleteInspection, toast]
  );

  const handleCompleteInspection = useCallback(
    async (inspection: SafetyInspection) => {
      if (!projectId) return;
      try {
        await completeInspection({
          projectId,
          inspectionId: inspection._id,
        }).unwrap();
        toast.success('Inspection marked as completed');
        refetchInspections();
      } catch (err: unknown) {
        const message =
          err &&
          typeof err === 'object' &&
          'data' in err &&
          typeof (err as { data?: { message?: string } }).data?.message === 'string'
            ? (err as { data: { message: string } }).data.message
            : 'Failed to complete inspection';
        toast.error(message);
      }
    },
    [projectId, completeInspection, toast, refetchInspections]
  );

  const handleCloseInspection = useCallback(
    async (inspection: SafetyInspection) => {
      if (!projectId) return;
      try {
        await closeInspection({
          projectId,
          inspectionId: inspection._id,
        }).unwrap();
        toast.success('Inspection closed');
        refetchInspections();
      } catch (err: unknown) {
        const message =
          err &&
          typeof err === 'object' &&
          'data' in err &&
          typeof (err as { data?: { message?: string } }).data?.message === 'string'
            ? (err as { data: { message: string } }).data.message
            : 'Failed to close inspection';
        toast.error(message);
      }
    },
    [projectId, closeInspection, toast, refetchInspections]
  );

  // ============================================================================
  // Handlers - Toolbox Talks
  // ============================================================================

  const handleCreateTalk = useCallback(() => {
    setSelectedTalk(null);
    setTalkDrawerMode('create');
    setIsTalkDrawerOpen(true);
  }, []);

  const handleEditTalk = useCallback((talk: ToolboxTalk) => {
    setSelectedTalk(talk);
    setTalkDrawerMode('edit');
    setIsTalkDrawerOpen(true);
  }, []);

  const handleDeleteTalk = useCallback(
    async (talk: ToolboxTalk) => {
      if (!projectId) return;
      if (!window.confirm(`Delete toolbox talk ${talk.talkNumber}? This cannot be undone.`)) {
        return;
      }
      try {
        await deleteTalk({ projectId, talkId: talk._id }).unwrap();
        toast.success('Toolbox talk deleted');
      } catch {
        toast.error('Failed to delete toolbox talk');
      }
    },
    [projectId, deleteTalk, toast]
  );

  const handleCompleteTalk = useCallback(
    async (talk: ToolboxTalk) => {
      if (!projectId) return;
      try {
        await completeTalk({ projectId, talkId: talk._id }).unwrap();
        toast.success('Toolbox talk marked as completed');
        refetchTalks();
      } catch {
        toast.error('Failed to complete toolbox talk');
      }
    },
    [projectId, completeTalk, toast, refetchTalks]
  );

  // ============================================================================
  // Handlers - Observations
  // ============================================================================

  const handleCreateObservation = useCallback(() => {
    setSelectedObservation(null);
    setObsDrawerMode('create');
    setIsObsDrawerOpen(true);
  }, []);

  const handleEditObservation = useCallback((observation: SafetyObservation) => {
    setSelectedObservation(observation);
    setObsDrawerMode('edit');
    setIsObsDrawerOpen(true);
  }, []);

  const handleDeleteObservation = useCallback(
    async (observation: SafetyObservation) => {
      if (!projectId) return;
      if (
        !window.confirm(
          `Delete observation ${observation.observationNumber}? This cannot be undone.`
        )
      ) {
        return;
      }
      try {
        await deleteObservation({
          projectId,
          observationId: observation._id,
        }).unwrap();
        toast.success('Observation deleted');
      } catch {
        toast.error('Failed to delete observation');
      }
    },
    [projectId, deleteObservation, toast]
  );

  const handleResolveObservation = useCallback(
    async (observation: SafetyObservation) => {
      if (!projectId) return;
      try {
        await resolveObservation({
          projectId,
          observationId: observation._id,
          data: {},
        }).unwrap();
        toast.success('Observation resolved');
        refetchObservations();
      } catch {
        toast.error('Failed to resolve observation');
      }
    },
    [projectId, resolveObservation, toast, refetchObservations]
  );

  // ============================================================================
  // Get primary action based on active tab
  // ============================================================================

  const primaryAction = useMemo(() => {
    switch (activeTab) {
      case 'inspections':
        return { label: 'New Inspection', onClick: handleCreateInspection };
      case 'toolbox_talks':
        return { label: 'New Talk', onClick: handleCreateTalk };
      case 'observations':
        return { label: 'Record Observation', onClick: handleCreateObservation };
    }
  }, [activeTab, handleCreateInspection, handleCreateTalk, handleCreateObservation]);

  const isLoading =
    (activeTab === 'inspections' && isLoadingInspections) ||
    (activeTab === 'toolbox_talks' && isLoadingTalks) ||
    (activeTab === 'observations' && isLoadingObservations);

  const isFetching =
    (activeTab === 'inspections' && isFetchingInspections) ||
    (activeTab === 'toolbox_talks' && isFetchingTalks) ||
    (activeTab === 'observations' && isFetchingObservations);

  // ============================================================================
  // Render Filters per Tab
  // ============================================================================

  const renderFilters = () => {
    if (activeTab === 'inspections') {
      return (
        <div className="flex items-center gap-2">
          <div className="w-36">
            <SearchableDropdown
              placeholder="Type"
              value={filterInspectionType}
              onChange={val => {
                setFilterInspectionType(val as InspectionType | '');
                setInspectionPage(1);
              }}
              options={INSPECTION_TYPE_FILTER_OPTIONS}
              searchable={false}
            />
          </div>
          <div className="w-40">
            <SearchableDropdown
              placeholder="Status"
              value={filterInspectionStatus}
              onChange={val => {
                setFilterInspectionStatus(val as InspectionStatus | '');
                setInspectionPage(1);
              }}
              options={INSPECTION_STATUS_FILTER_OPTIONS}
              searchable={false}
            />
          </div>
          {(filterInspectionType || filterInspectionStatus) && (
            <button
              onClick={() => {
                setFilterInspectionType('');
                setFilterInspectionStatus('');
                setInspectionPage(1);
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      );
    }

    if (activeTab === 'toolbox_talks') {
      return (
        <div className="flex items-center gap-2">
          <div className="w-44">
            <SearchableDropdown
              placeholder="Category"
              value={filterTalkCategory}
              onChange={val => {
                setFilterTalkCategory(val as ToolboxTalkCategory | '');
                setTalkPage(1);
              }}
              options={TALK_CATEGORY_FILTER_OPTIONS}
              searchable={false}
            />
          </div>
          {filterTalkCategory && (
            <button
              onClick={() => {
                setFilterTalkCategory('');
                setTalkPage(1);
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      );
    }

    if (activeTab === 'observations') {
      return (
        <div className="flex items-center gap-2">
          <div className="w-36">
            <SearchableDropdown
              placeholder="Severity"
              value={filterObsSeverity}
              onChange={val => {
                setFilterObsSeverity(val as ObservationSeverity | '');
                setObservationPage(1);
              }}
              options={OBSERVATION_SEVERITY_FILTER_OPTIONS}
              searchable={false}
            />
          </div>
          <div className="w-36">
            <SearchableDropdown
              placeholder="Status"
              value={filterObsStatus}
              onChange={val => {
                setFilterObsStatus(val as ObservationStatus | '');
                setObservationPage(1);
              }}
              options={OBSERVATION_STATUS_FILTER_OPTIONS}
              searchable={false}
            />
          </div>
          {(filterObsSeverity || filterObsStatus) && (
            <button
              onClick={() => {
                setFilterObsSeverity('');
                setFilterObsStatus('');
                setObservationPage(1);
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      );
    }

    return null;
  };

  // ============================================================================
  // Pagination Renderer
  // ============================================================================

  const renderPagination = (
    pagination: { page: number; pages: number; total: number; hasNext: boolean; hasPrev: boolean } | undefined,
    onPageChange: (page: number) => void
  ) => {
    if (!pagination || pagination.pages <= 1) return null;
    return (
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Page {pagination.page} of {pagination.pages} &bull; {pagination.total} total
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={!pagination.hasPrev}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={!pagination.hasNext}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Render Inspections Tab
  // ============================================================================

  const renderInspections = () => {
    if (isLoadingInspections) return <LoadingSpinner />;

    if (!inspections.length) {
      return (
        <EmptyState
          icon={ClipboardList}
          title="No safety inspections"
          description="Create the first safety inspection for this project to track compliance and safety standards."
          action={{ label: 'New Inspection', onClick: handleCreateInspection }}
        />
      );
    }

    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Number
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Date
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Type
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Location
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Score
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Result
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Status
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Inspector
                </th>
                <th className="w-12 py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {inspections.map(inspection => {
                const menuItems: (ActionMenuItem | 'divider')[] = [
                  {
                    label: 'Edit',
                    icon: <Pencil className="h-3.5 w-3.5" />,
                    onClick: () => handleEditInspection(inspection),
                    hidden: inspection.status === 'closed',
                  },
                  {
                    label: 'Mark Complete',
                    icon: <CheckCircle className="h-3.5 w-3.5" />,
                    onClick: () => handleCompleteInspection(inspection),
                    hidden: ['completed', 'reviewed', 'closed'].includes(inspection.status),
                  },
                  {
                    label: 'Close Inspection',
                    icon: <X className="h-3.5 w-3.5" />,
                    onClick: () => handleCloseInspection(inspection),
                    hidden: inspection.status === 'closed' || inspection.status === 'draft',
                    variant: 'warning' as const,
                  },
                  'divider',
                  {
                    label: 'Delete',
                    icon: <Trash2 className="h-3.5 w-3.5" />,
                    onClick: () => handleDeleteInspection(inspection),
                    variant: 'danger' as const,
                    hidden: inspection.status === 'closed',
                  },
                ];

                return (
                  <tr
                    key={inspection._id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleEditInspection(inspection)}
                  >
                    <td className="py-2.5 px-3">
                      <span className="text-xs font-mono text-gray-600">
                        {inspection.inspectionNumber}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-700">
                      {formatDate(inspection.inspectionDate)}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs text-gray-700">
                        {formatInspectionType(inspection.inspectionType)}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs text-gray-700 max-w-[150px] truncate block">
                        {inspection.location}
                        {inspection.area && (
                          <span className="text-gray-400"> / {inspection.area}</span>
                        )}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      {inspection.status !== 'draft' ? (
                        <span
                          className={`text-sm font-semibold ${
                            inspection.score >= 90
                              ? 'text-green-600'
                              : inspection.score >= 75
                              ? 'text-yellow-600'
                              : inspection.score >= 60
                              ? 'text-orange-600'
                              : 'text-red-600'
                          }`}
                        >
                          {inspection.score}%
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      {inspection.status !== 'draft' ? (
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${getOverallStatusBadge(
                            inspection.overallStatus
                          )}`}
                        >
                          {inspection.overallStatus.replace(/_/g, ' ')}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      <StatusBadge
                        variant={getInspectionStatusVariant(inspection.status)}
                        status={getInspectionStatusLabel(inspection.status)}
                      />
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-500">
                      {inspection.inspectorName}
                    </td>
                    <td
                      className="py-2.5 px-3"
                      onClick={e => e.stopPropagation()}
                    >
                      <ActionMenu
                        id={`inspection-${inspection._id}`}
                        items={menuItems}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPagination(inspectionPagination, setInspectionPage)}
      </>
    );
  };

  // ============================================================================
  // Render Toolbox Talks Tab
  // ============================================================================

  const renderToolboxTalks = () => {
    if (isLoadingTalks) return <LoadingSpinner />;

    if (!talks.length) {
      return (
        <EmptyState
          icon={MessageSquare}
          title="No toolbox talks"
          description="Record safety toolbox talks and track attendance for this project."
          action={{ label: 'New Talk', onClick: handleCreateTalk }}
        />
      );
    }

    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Number
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Date
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Topic
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Category
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Conducted By
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Attendees
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Duration
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Status
                </th>
                <th className="w-12 py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {talks.map(talk => {
                const menuItems: (ActionMenuItem | 'divider')[] = [
                  {
                    label: 'Edit',
                    icon: <Pencil className="h-3.5 w-3.5" />,
                    onClick: () => handleEditTalk(talk),
                  },
                  {
                    label: 'Mark Complete',
                    icon: <CheckCircle className="h-3.5 w-3.5" />,
                    onClick: () => handleCompleteTalk(talk),
                    hidden: talk.status === 'completed',
                  },
                  'divider',
                  {
                    label: 'Delete',
                    icon: <Trash2 className="h-3.5 w-3.5" />,
                    onClick: () => handleDeleteTalk(talk),
                    variant: 'danger' as const,
                  },
                ];

                return (
                  <tr
                    key={talk._id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleEditTalk(talk)}
                  >
                    <td className="py-2.5 px-3">
                      <span className="text-xs font-mono text-gray-600">{talk.talkNumber}</span>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-700">
                      {formatDate(talk.talkDate)}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs text-gray-900 font-medium max-w-[200px] truncate block">
                        {talk.topic}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs text-gray-600">
                        {formatTalkCategory(talk.category)}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-600">
                      {talk.conductedByName}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs font-medium text-gray-700">
                        {talk.attendees.length}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-600">
                      {talk.duration ? `${talk.duration} min` : '—'}
                    </td>
                    <td className="py-2.5 px-3">
                      <StatusBadge
                        variant={talk.status === 'completed' ? 'success' : 'neutral'}
                        status={talk.status === 'completed' ? 'Completed' : 'Draft'}
                      />
                    </td>
                    <td
                      className="py-2.5 px-3"
                      onClick={e => e.stopPropagation()}
                    >
                      <ActionMenu id={`talk-${talk._id}`} items={menuItems} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPagination(talkPagination, setTalkPage)}
      </>
    );
  };

  // ============================================================================
  // Render Observations Tab
  // ============================================================================

  const renderObservations = () => {
    if (isLoadingObservations) return <LoadingSpinner />;

    if (!observations.length) {
      return (
        <EmptyState
          icon={Eye}
          title="No safety observations"
          description="Record safety observations to track safe and unsafe behaviors on site."
          action={{ label: 'Record Observation', onClick: handleCreateObservation }}
        />
      );
    }

    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Number
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Date
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Category
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3 w-64">
                  Description
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Location
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Severity
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Status
                </th>
                <th className="text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide py-2 px-3">
                  Observed By
                </th>
                <th className="w-12 py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {observations.map(obs => {
                const menuItems: (ActionMenuItem | 'divider')[] = [
                  {
                    label: 'Edit',
                    icon: <Pencil className="h-3.5 w-3.5" />,
                    onClick: () => handleEditObservation(obs),
                    hidden: obs.status === 'closed',
                  },
                  {
                    label: 'Resolve',
                    icon: <CheckCircle className="h-3.5 w-3.5" />,
                    onClick: () => handleResolveObservation(obs),
                    hidden: ['resolved', 'closed'].includes(obs.status),
                    variant: 'success' as const,
                  },
                  'divider',
                  {
                    label: 'Delete',
                    icon: <Trash2 className="h-3.5 w-3.5" />,
                    onClick: () => handleDeleteObservation(obs),
                    variant: 'danger' as const,
                    hidden: obs.status === 'closed',
                  },
                ];

                return (
                  <tr
                    key={obs._id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleEditObservation(obs)}
                  >
                    <td className="py-2.5 px-3">
                      <span className="text-xs font-mono text-gray-600">
                        {obs.observationNumber}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-700">
                      {formatDate(obs.observationDate)}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs text-gray-700 capitalize">
                        {obs.category.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs text-gray-700 truncate block max-w-[240px]">
                        {obs.description}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-600">{obs.location}</td>
                    <td className="py-2.5 px-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${getObservationSeverityBadge(
                          obs.severity
                        )}`}
                      >
                        {obs.severity}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <StatusBadge
                        variant={getObservationStatusVariant(obs.status)}
                        status={obs.status.replace(/_/g, ' ')}
                      />
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-600">
                      {obs.observedByName}
                    </td>
                    <td
                      className="py-2.5 px-3"
                      onClick={e => e.stopPropagation()}
                    >
                      <ActionMenu id={`obs-${obs._id}`} items={menuItems} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPagination(observationPagination, setObservationPage)}
      </>
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (!projectId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <EmptyState
          icon={Shield}
          title="No project selected"
          description="Select a project to view its safety reports."
        />
      </div>
    );
  }

  return (
    <>
      <ModuleLayout
        title="Safety Reports"
        description="Manage inspections, toolbox talks, and safety observations"
        icon={<Shield className="h-4 w-4 text-brand-600" />}
        primaryAction={primaryAction}
        stats={headerStats}
        searchValue={
          activeTab === 'inspections'
            ? inspectionSearch
            : activeTab === 'toolbox_talks'
            ? talkSearch
            : observationSearch
        }
        onSearchChange={value => {
          if (activeTab === 'inspections') {
            setInspectionSearch(value);
            setInspectionPage(1);
          } else if (activeTab === 'toolbox_talks') {
            setTalkSearch(value);
            setTalkPage(1);
          } else {
            setObservationSearch(value);
            setObservationPage(1);
          }
        }}
        filters={renderFilters()}
        isLoading={isFetching}
        onRefresh={() => {
          if (activeTab === 'inspections') refetchInspections();
          else if (activeTab === 'toolbox_talks') refetchTalks();
          else refetchObservations();
        }}
      >
        {/* Stats Cards row */}
        {stats && (
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <ClipboardList className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-700">Avg Inspection Score</span>
              </div>
              <div
                className={`text-2xl font-bold ${
                  stats.averageScore >= 90
                    ? 'text-green-600'
                    : stats.averageScore >= 75
                    ? 'text-yellow-600'
                    : 'text-red-600'
                }`}
              >
                {stats.averageScore}%
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <span className="text-xs font-medium text-orange-700">Open Observations</span>
              </div>
              <div className="text-2xl font-bold text-orange-600">{stats.openObservations}</div>
              {stats.criticalObservations > 0 && (
                <p className="text-[10px] text-red-600 mt-0.5">
                  {stats.criticalObservations} critical
                </p>
              )}
            </div>

            <div className="bg-green-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="h-4 w-4 text-green-600" />
                <span className="text-xs font-medium text-green-700">Talks This Month</span>
              </div>
              <div className="text-2xl font-bold text-green-600">
                {stats.toolboxTalksThisMonth}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-4 w-4 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">OSHA Recordable</span>
              </div>
              <div className="text-2xl font-bold text-gray-700">{stats.trir}</div>
              <p className="text-[10px] text-gray-400 mt-0.5">incidents</p>
            </div>
          </div>
        )}

        {/* Tab Bar */}
        <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
          {(
            [
              { id: 'inspections', label: 'Inspections', icon: ClipboardList },
              { id: 'toolbox_talks', label: 'Toolbox Talks', icon: MessageSquare },
              { id: 'observations', label: 'Observations', icon: Eye },
            ] as const
          ).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-3">
            {activeTab === 'inspections' && renderInspections()}
            {activeTab === 'toolbox_talks' && renderToolboxTalks()}
            {activeTab === 'observations' && renderObservations()}
          </div>
        )}
      </ModuleLayout>

      {/* Drawers */}
      <SafetyInspectionDrawer
        isOpen={isInspectionDrawerOpen}
        onClose={() => setIsInspectionDrawerOpen(false)}
        projectId={projectId || ''}
        inspection={selectedInspection}
        mode={inspectionDrawerMode}
      />

      <ToolboxTalkDrawer
        isOpen={isTalkDrawerOpen}
        onClose={() => setIsTalkDrawerOpen(false)}
        projectId={projectId || ''}
        talk={selectedTalk}
        mode={talkDrawerMode}
      />

      <SafetyObservationDrawer
        isOpen={isObsDrawerOpen}
        onClose={() => setIsObsDrawerOpen(false)}
        projectId={projectId || ''}
        observation={selectedObservation}
        mode={obsDrawerMode}
      />
    </>
  );
};

export default SafetyReports;
